import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { apiFetch } from '../services/api';
import { properties } from '../services/api';
import GardenModal from './GardenModal';
import yardrayLogo from '../assets/yardray-logo.png';
import './Tasks.css';

function formatAppDate(dateStr) {
  if (!dateStr) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length === 3) {
    return `${parts[2].padStart(2,"0")}-${months[parseInt(parts[1],10)-1]}-${parts[0]}`;
  }
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2,"0")}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState(localStorage.getItem('gardenbuddy_selected_property') || 'all');
  const [userProperties, setUserProperties] = useState([]);
  const [modal, setModal] = useState({ isOpen: false });
  const navigate = useNavigate();

  useEffect(() => {
    loadTasks();
    loadProperties();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await api.get('/tasks');
      setTasks(response.data.tasks);
      setLoading(false);
    } catch (error) {
      console.error('Error loading tasks:', error);
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    try {
      const res = await properties.getAll();
      setUserProperties(res.data.properties);
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await api.patch(`/tasks/${taskId}/complete`, {});
      loadTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to complete task', onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  const handleDelete = async (taskId) => {
    setModal({ isOpen: true, type: 'confirm', message: 'Delete this task?', onConfirm: async () => {
      setModal({ isOpen: false });
      await api.delete(`/tasks/${taskId}`);
      loadTasks();
    }, onCancel: () => setModal({ isOpen: false }) });
  };

  const handleUncomplete = async (taskId) => {
    try {
      await api.patch(`/tasks/${taskId}/uncomplete`, {});
      loadTasks();
    } catch (error) {
      console.error('Error uncompleting task:', error);
      setModal({ isOpen: true, type: 'alert', title: 'Error', message: 'Failed to uncomplete task', onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  const handleReschedule = async (taskId, newDate) => {
    try {
      await api.patch(`/tasks/${taskId}/reschedule`, { scheduledFor: newDate });
      loadTasks();
    } catch (error) {
      console.error("Reschedule task error:", error);
      setModal({ isOpen: true, type: "alert", title: "Error", message: "Failed to reschedule task", onConfirm: () => setModal({ isOpen: false }) });
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (propertyFilter !== 'all' && String(task.property_id) !== String(propertyFilter)) return false;
    if (filter === 'pending') return task.status === 'pending';
    if (filter === 'completed') return task.status === 'completed';
    return true;
  });

  const upcomingTasks = filteredTasks.filter(t => {
    const taskDate = t.scheduled_for.split('T')[0];
    const todayDate = new Date().toISOString().split('T')[0];
    return taskDate > todayDate && t.status === 'pending';
  });

  const overdueTasks = filteredTasks.filter(t => {
    const taskDate = t.scheduled_for.split('T')[0];
    const todayDate = new Date().toISOString().split('T')[0];
    return taskDate < todayDate && t.status === 'pending';
  });

  const completedTasks = filteredTasks.filter(t => t.status === 'completed');
  const skippedTasks = filteredTasks.filter(t => t.status === 'skipped');
  const todayStr = new Date().toISOString().split('T')[0];
  const dueTodayTasks = filteredTasks.filter(t =>
    t.scheduled_for.split('T')[0] === todayStr && t.status === 'pending'
  );

  if (loading) {
    return <div className="tasks-loading">Loading tasks...</div>;
  }

  return (
    <div className="tasks-container">
      <h2>Garden Tasks</h2>
      <GardenModal isOpen={modal.isOpen} type={modal.type} title={modal.title} message={modal.message} onConfirm={modal.onConfirm} onCancel={() => setModal({ isOpen: false })} />

      {userProperties.length > 1 && (
        <div className="tasks-prop-filter">
          <select
            className="tasks-prop-select"
            value={propertyFilter}
            onChange={e => { setPropertyFilter(e.target.value); localStorage.setItem('gardenbuddy_selected_property', e.target.value); }}
          >
            <option value="all">All Properties</option>
            {userProperties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      )}

      <div className="tasks-filter-row">
        <div className="tasks-filter-btns">
          <button className={`tasks-filter-btn${filter === 'all' ? ' tasks-filter-btn--active' : ''}`} onClick={() => setFilter('all')}>
            All ({tasks.length})
          </button>
          <button className={`tasks-filter-btn${filter === 'pending' ? ' tasks-filter-btn--active' : ''}`} onClick={() => setFilter('pending')}>
            Pending ({upcomingTasks.length + overdueTasks.length + dueTodayTasks.length})
          </button>
          <button className={`tasks-filter-btn${filter === 'completed' ? ' tasks-filter-btn--active' : ''}`} onClick={() => setFilter('completed')}>
            Completed ({completedTasks.length})
          </button>
        </div>
        <button className="tasks-add-btn" onClick={() => navigate('/tasks/add')}>
          + Add Task
        </button>
      </div>

      <div className="tasks-generate-wrap">
        <button className="tasks-generate-btn" onClick={() => navigate('/properties')}>
          🌿 Generate Lawn Care Tasks
        </button>
      </div>

      {tasks.length === 0 && userProperties.length === 0 && (
        <div className="tasks-empty">
          <img src={yardrayLogo} alt="Yardray" className="tasks-empty-logo" />
          <p className="tasks-empty-p--mb">Add a property to start tracking tasks.</p>
          <button className="tasks-empty-btn" onClick={() => navigate('/properties')}>Add Property</button>
        </div>
      )}
      {tasks.length === 0 && userProperties.length > 0 && (
        <div className="tasks-empty">
          <p>No tasks yet. Add a task or generate lawn care tasks from the Add Task page.</p>
        </div>
      )}

      {dueTodayTasks.length > 0 && (
        <div className="tasks-section tasks-section--due-today">
          <h3>Due Today ({dueTodayTasks.length})</h3>
          {dueTodayTasks.map(task => (
            <TaskCard key={task.id} task={task} onComplete={handleComplete} onDelete={handleDelete} onUncomplete={handleUncomplete} onReschedule={handleReschedule} highlight={true} />
          ))}
        </div>
      )}

      {overdueTasks.length > 0 && (
        <div className="tasks-section tasks-section--overdue">
          <h3>⚠️ Overdue ({overdueTasks.length})</h3>
          {overdueTasks.map(task => (
            <TaskCard key={task.id} task={task} onComplete={handleComplete} onDelete={handleDelete} onUncomplete={handleUncomplete} onReschedule={handleReschedule} />
          ))}
        </div>
      )}

      {upcomingTasks.length > 0 && (
        <div className="tasks-section">
          <h3>📅 Upcoming ({upcomingTasks.length})</h3>
          {upcomingTasks.map(task => (
            <TaskCard key={task.id} task={task} onComplete={handleComplete} onDelete={handleDelete} onUncomplete={handleUncomplete} onReschedule={handleReschedule} />
          ))}
        </div>
      )}

      {skippedTasks.length > 0 && filter === 'all' && (
        <div className="tasks-section">
          <h3>⏭️ Skipped ({skippedTasks.length})</h3>
          {skippedTasks.map(task => (
            <TaskCard key={task.id} task={task} onComplete={handleComplete} onDelete={handleDelete} onUncomplete={handleUncomplete} onReschedule={handleReschedule} />
          ))}
        </div>
      )}

      {completedTasks.length > 0 && (filter === 'all' || filter === 'completed') && (
        <div className="tasks-section">
          <h3>✅ Completed ({completedTasks.length})</h3>
          {completedTasks.map(task => (
            <TaskCard key={task.id} task={task} onComplete={handleComplete} onDelete={handleDelete} onUncomplete={handleUncomplete} onReschedule={handleReschedule} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, onComplete, onDelete, onUncomplete, onReschedule, highlight = false }) {
  const isOverdue = new Date(task.scheduled_for) < new Date() && task.status === 'pending';
  const isManual = task.created_by === 'user';
  const isLawnCare = task.created_by === 'lawn_care_automation';
  const metadata = task.metadata || {};
  const [expanded, setExpanded] = useState(false);
  const [editingDate, setEditingDate] = useState(false);

  const handleProductClick = async (productName, retailer) => {
    const searchOverrides = {
      'Sedgehammer': 'Sedgehammer nutsedge herbicide',
      'Beneficial nematodes': 'beneficial nematodes grub control lawn',
      'Beneficial Nematodes': 'beneficial nematodes grub control lawn',
      'Corn Gluten Meal': 'corn gluten meal lawn pre-emergent weed control',
      'Bonide Weed Killer Plus (vinegar-based)': 'Bonide Weed Killer organic vinegar',
      'Compost': 'lawn compost topdressing organic',
      'Compost topdressing': 'lawn compost topdressing organic',
      'Pelletized Lime': 'pelletized lime lawn soil',
      'Fast Acting Lime': 'fast acting lime lawn',
      'Espoma Organic': 'Espoma organic lawn fertilizer',
      'Espoma Fall': 'Espoma fall lawn fertilizer',
      'Espoma Fall Lawn Food': 'Espoma fall lawn fertilizer',
      'Espoma Organic Lawn Food': 'Espoma organic lawn fertilizer',
      'Milorganite': 'Milorganite organic lawn fertilizer',
    };
    const searchQuery = searchOverrides[productName] || productName;
    try {
      const response = await apiFetch(`${process.env.REACT_APP_API_URL}/product-clicks/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskId: task.id, productName: searchQuery, retailer, zone: metadata.zone })
      });
      const data = await response.json();
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Product click error:', error);
      window.open(`https://www.google.com/search?q=${encodeURIComponent(searchQuery)}`, '_blank');
    }
  };

  const statusClass = task.status === 'completed' ? 'task-status-badge--completed'
    : task.status === 'skipped' ? 'task-status-badge--skipped'
    : isOverdue ? 'task-status-badge--overdue'
    : 'task-status-badge--pending';

  const statusLabel = task.status === 'completed' ? 'Completed'
    : task.status === 'skipped' ? 'Skipped'
    : isOverdue ? 'Overdue' : 'Pending';

  return (
    <div
      className={`task-card${isOverdue ? ' task-card--overdue' : ''}${highlight ? ' task-card--highlight' : ''}`}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="task-card-inner">
        <div>
          <div className="task-card-title-row">
            <h4>{task.title}</h4>
            {isManual && <span className="task-badge task-badge--manual">Manual</span>}
            {isLawnCare && <span className="task-badge task-badge--lawn">🌿 Lawn Care</span>}
            {task.created_by === 'weather_automation' && <span className="task-badge task-badge--weather">☁️ Weather</span>}
          </div>

          {task.property_name && (
            <p className="task-card-property">Property: {task.property_name}</p>
          )}
          {task.plant_name && (
            <p className="task-card-plant">Plant: {task.plant_name}</p>
          )}

          <p className="task-card-desc">{task.description}</p>

          {expanded && (
            <div>
              {metadata.product_recommendations && metadata.product_recommendations.length > 0 && (
                <div className="task-products">
                  <p className="task-products-label">💡 Recommended Products:</p>
                  <div className="task-product-list">
                    {metadata.product_recommendations.map((product, idx) => (
                      <div key={idx} className="task-product-item">
                        <div className="task-product-name">{product}</div>
                        <div className="task-product-btns">
                          <button className="task-btn-amazon" onClick={() => handleProductClick(product, 'amazon')}>Amazon</button>
                          <button className="task-btn-lowes" onClick={() => handleProductClick(product, 'lowes')}>Lowe's</button>
                          <button className="task-btn-homedepot" onClick={() => handleProductClick(product, 'homedepot')}>Home Depot</button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {metadata.organic_options && metadata.organic_options.length > 0 && (
                    <div className="task-organic">
                      <p className="task-organic-label">🌱 {metadata.pet_friendly ? 'Pet-Friendly' : 'Organic'} Alternatives:</p>
                      <div className="task-product-list">
                        {metadata.organic_options.map((option, idx) => (
                          <div key={idx} className="task-product-item">
                            <div className="task-organic-item-name">{option}</div>
                            <div className="task-product-btns">
                              <button className="task-btn-amazon" onClick={() => handleProductClick(option, 'amazon')}>Amazon</button>
                              <button className="task-btn-lowes" onClick={() => handleProductClick(option, 'lowes')}>Lowe's</button>
                              <button className="task-btn-homedepot" onClick={() => handleProductClick(option, 'homedepot')}>Home Depot</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {editingDate ? (
            <div className="task-reschedule-wrap">
              <input
                className="task-date-input"
                type="date"
                defaultValue={task.scheduled_for.split('T')[0]}
                onChange={(e) => { if (e.target.value) { onReschedule(task.id, e.target.value); setEditingDate(false); } }}
                autoFocus
              />
              <button className="task-reschedule-cancel" onClick={() => setEditingDate(false)}>Cancel</button>
            </div>
          ) : (
            <p className="task-card-date">
              Due: {formatAppDate(task.scheduled_for)}
              {metadata.zone && ` • Zone ${metadata.zone}`}
              {task.recurrence_rule && (
                <span className="task-recurrence">🔁 Repeats {task.recurrence_rule}</span>
              )}
              {task.status === 'pending' && (
                <button className="task-edit-date-btn" onClick={() => setEditingDate(true)}>Edit date</button>
              )}
            </p>
          )}
        </div>

        <div className="task-card-footer">
          <span className={`task-status-badge ${statusClass}`}>{statusLabel}</span>

          {task.status === 'pending' && (
            <div className="task-card-actions">
              <button className="task-btn-edit" onClick={(e) => { e.stopPropagation(); window.location.href = `/tasks/${task.id}/edit`; }}>
                ✏️ Edit
              </button>
              <button className="task-btn-complete" onClick={() => onComplete(task.id)}>
                ✓ Complete
              </button>
              <button className="task-btn-delete" onClick={() => onDelete(task.id)}>
                Delete
              </button>
            </div>
          )}

          {task.status === 'completed' && (
            <button className="task-btn-undo" onClick={() => onUncomplete(task.id)}>
              ↶ Undo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Tasks;
